import React, { useEffect, useState } from "react";
import { AlertTriangle, Plus, X, Clock } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { fmtDate } from "../lib/format.js";

const STATE_STYLE = {
  "Nova": "bg-red-50 text-red-800 border-red-300",
  "Em análise": "bg-amber-50 text-amber-800 border-amber-300",
  "Acção necessária": "bg-orange-50 text-orange-800 border-orange-300",
  "Aguarda cliente": "bg-blue-50 text-blue-800 border-blue-300",
  "Resolvida": "bg-emerald-50 text-emerald-800 border-emerald-300",
  "Fechada": "bg-gray-100 text-gray-700 border-gray-300",
};

export default function ComplaintsPage() {
  const [rows, setRows] = useState([]);
  const [enums, setEnums] = useState(null);
  const [filter, setFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showResolve, setShowResolve] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    const q = new URLSearchParams();
    if (filter) q.append("state", filter);
    if (catFilter) q.append("category", catFilter);
    const [{ data }, { data: e }] = await Promise.all([
      api.get("/crm/complaints?" + q),
      api.get("/crm/meta/enums"),
    ]);
    setRows(data); setEnums(e);
  };
  useEffect(() => { load(); }, [filter, catFilter]);

  const doTransition = async (id, newState, extra = {}) => {
    try {
      await api.post(`/crm/complaints/${id}/state`, { new_state: newState, ...extra });
      load();
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2"><AlertTriangle className="w-6 h-6 text-[#DC3545]" /> Reclamações</h1>
          <p className="text-sm text-[#6C757D] mt-1">Gestão multi-departamento com SLA e escalonamento automático.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="text-sm bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-2 rounded-sm flex items-center gap-2" data-testid="complaint-new-btn">
          <Plus className="w-4 h-4" /> Nova reclamação
        </button>
      </div>

      {err && <div className="mb-3 px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">{err}</div>}

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 rounded-sm border ${filter === "" ? "bg-[#1F3864] text-white border-[#1F3864]" : "bg-white border-gray-300"}`}>Todos estados</button>
        {enums?.complaint_states?.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 rounded-sm border ${filter === s ? "bg-[#1F3864] text-white border-[#1F3864]" : "bg-white border-gray-300"}`}>{s}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setCatFilter("")} className={`text-xs px-3 py-1.5 rounded-sm border ${catFilter === "" ? "bg-[#1F3864] text-white border-[#1F3864]" : "bg-white border-gray-300"}`}>Todas categorias</button>
        {enums?.complaint_categories?.map((c) => (
          <button key={c} onClick={() => setCatFilter(c)} className={`text-xs px-3 py-1.5 rounded-sm border ${catFilter === c ? "bg-[#1F3864] text-white border-[#1F3864]" : "bg-white border-gray-300"}`}>{c}</button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] border-b border-gray-200">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">#</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Cliente</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Categoria</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Assunto</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Estado</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">SLA</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Aberto</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Acção</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-[#6C757D] italic px-3 py-6">Sem reclamações.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-gray-100 hover:bg-[#FAFBFC]" data-testid={`complaint-row-${r.number}`}>
                <td className="px-3 py-2 font-mono text-[#1F3864] font-semibold">{r.number}</td>
                <td className="px-3 py-2">{r.customer?.name || r.customer_name}</td>
                <td className="px-3 py-2 text-xs">{r.category}</td>
                <td className="px-3 py-2 text-xs truncate max-w-xs" title={r.subject}>{r.subject}</td>
                <td className="px-3 py-2"><span className={`text-[11px] uppercase font-semibold border rounded-sm px-2 py-0.5 ${STATE_STYLE[r.current_state] || "bg-gray-100"}`}>{r.current_state}</span></td>
                <td className="px-3 py-2 text-xs">
                  {r.is_overdue ? (
                    <span className="text-[#DC3545] font-bold flex items-center gap-1"><Clock className="w-3 h-3" />L{r.escalation_level}</span>
                  ) : r.escalation_level === 0 ? (
                    <span className="text-[#28A745]">Ok</span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 text-xs text-[#6C757D]">{fmtDate(r.opened_at)}</td>
                <td className="px-3 py-2">
                  {r.current_state === "Nova" && <button onClick={() => doTransition(r.id, "Em análise")} className="text-xs border border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864] hover:text-white rounded-sm px-2 py-1" data-testid={`complaint-start-${r.number}`}>Analisar</button>}
                  {["Em análise", "Acção necessária", "Aguarda cliente"].includes(r.current_state) && <button onClick={() => setShowResolve(r)} className="text-xs border border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white rounded-sm px-2 py-1" data-testid={`complaint-resolve-${r.number}`}>Resolver</button>}
                  {r.current_state === "Resolvida" && <button onClick={() => doTransition(r.id, "Fechada")} className="text-xs border border-gray-500 text-gray-700 hover:bg-gray-600 hover:text-white rounded-sm px-2 py-1">Fechar</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && enums && <NewComplaintDialog enums={enums} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {showResolve && <ResolveDialog complaint={showResolve} onClose={() => setShowResolve(null)} onSaved={(resolution) => { doTransition(showResolve.id, "Resolvida", { resolution }); setShowResolve(null); }} />}
    </div>
  );
}

function NewComplaintDialog({ enums, onClose, onSaved }) {
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ category: "Serviço", subject: "", description: "", priority: "Normal" });
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/customers?limit=100").then((r) => setCustomers(r.data)); }, []);
  const submit = async () => {
    try {
      await api.post("/crm/complaints", { customer_id: customerId, ...form });
      onSaved();
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="complaint-new-dialog">
      <div className="bg-white rounded-md border border-gray-300 w-full max-w-lg">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between"><h3 className="font-heading font-bold">Nova reclamação</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
        <div className="p-4 space-y-3">
          {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">{err}</div>}
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Cliente *</span>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="complaint-customer-select">
              <option value="">—</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.customer_number} · {c.name}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Categoria</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm">{enums.complaint_categories.map((c) => <option key={c}>{c}</option>)}</select>
          </label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Assunto</span>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="complaint-subject" />
          </label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Descrição</span>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="complaint-description" />
          </label>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-gray-300 rounded-sm">Cancelar</button>
          <button onClick={submit} className="text-sm bg-[#1F3864] hover:bg-[#142542] text-white px-4 py-1.5 rounded-sm" data-testid="complaint-save-btn">Guardar</button>
        </div>
      </div>
    </div>
  );
}

function ResolveDialog({ complaint, onClose, onSaved }) {
  const [resolution, setResolution] = useState("");
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md border border-gray-300 w-full max-w-md">
        <div className="px-4 py-3 border-b border-gray-200"><h3 className="font-heading font-bold">Resolver {complaint.number}</h3></div>
        <div className="p-4"><textarea rows={4} placeholder="Descrição da resolução…" value={resolution} onChange={(e) => setResolution(e.target.value)} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="complaint-resolution-input" /></div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-gray-300 rounded-sm">Cancelar</button>
          <button onClick={() => onSaved(resolution)} disabled={!resolution.trim()} className="text-sm bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-1.5 rounded-sm disabled:opacity-50" data-testid="complaint-confirm-resolve">Resolver (envia CES)</button>
        </div>
      </div>
    </div>
  );
}
